<template>
  <AppWithLoading>
    <template>
      <Preloader v-if="isLoading || !vacancy" fullscreen />
      <div
        v-else
        class="vacancy-candidates-root"
      >
        <div
          class="vacancy-candidates-page"
          :class="{ 'vacancy-candidates-page--blurred': isExpired && $route.name !== 'candidates' }"
        >
          <div class="vacancy-candidates__content">
            <Preloader v-if="operationLoader" class="vacancy-candidates__operation-preloader" />
            <PrefetchInterview v-if="vacancy" resolution="144p" :vacancy="vacancy" />
            <VacancyCandidatesKanban
              v-if="countCandidatesByStatus"
              :vacancy="vacancy"
              :candidates="candidates"
              :count-candidates-by-status="countCandidatesByStatus"
              :is-loading="isLoading"
              :hide-tariff-overlay="true"
              @getPaginatedCandidates="getPaginatedCandidates"
              @enableOperationLoader="enableOperationLoader"
              @disableOperationLoader="disableOperationLoader"
              @getCountCandidates="getCountCandidates"
              @messagesSent="showMessagesSentToast"
            />
          </div>
        </div>
        <div
          v-if="isExpired && $route.name !== 'candidates'"
          class="vacancy-candidates-tariff-overlay"
        >
        <VCard class="vacancy-candidates-tariff-card">
          <VCardTitle class="vacancy-candidates-tariff-header">
            <img
              :src="require('@/assets/img/tarrif-change-warning.svg')"
              alt=""
              class="vacancy-candidates-tariff-icon"
            >
            <div class="vacancy-candidates-tariff-title">
              {{ $t('tariffInactiveTitle') }}
            </div>
          </VCardTitle>
          <VCardText class="vacancy-candidates-tariff-text">
            {{ $t('tariffInactiveText') }}
          </VCardText>
          <VBtn
            class="vacancy-candidates-tariff-btn"
            color="success"
            @click="redirectToUpgradePlan"
          >
            {{ $t('tariffInactiveButton') }}
          </VBtn>
        </VCard>
        </div>
      </div>
      <template v-if="hasIntegrated">
        <IntegrationsHelpDialogKanbanAuto v-if="autoInvitation" />
        <IntegrationsHelpDialogKanban v-else />
      </template>
      <router-view />
    </template>
  </AppWithLoading>
</template>

<script>
import PrefetchInterview from '@huntica/huntica-interview-module/src/components/PrefetchInterview.vue';
import IntegrationsHelpDialogKanban from '@/components/Integrations/IntegrationsHelpDialogKanban';
import Vue from 'vue';
import {
  computed, ref, onMounted, toRefs, nextTick,
} from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import IntegrationsHelpDialogKanbanAuto from '@/components/Integrations/IntegrationsHelpDialogKanbanAuto';
import useCandidates from '@/compositions/useCandidates';
import useVacancyCandidatesByStatus from '@/compositions/useVacancyCandidatesByStatus';
import useNotificationTemplateSettings from '@/compositions/useNotificationTemplateSettings';
import VacancyCandidatesCountByStatus from '@/orm/models/Vacancy/VacancyCandidatesCountByStatus';
import Preloader from '../Preloader.vue';
import VacancyCandidatesKanban from './VacancyCandidatesKanban.vue';
import AppWithLoading from '../AppWithLoading.vue';
import useSubscription from '@/compositions/accounting/useSubscription';
import { paymentTabs, settingsTabs } from '@/enums';

export default {
  name: 'VacancyCandidates',
  metaInfo() {
    return {
      title: this.$t('candidates'),
    };
  },
  components: {
    AppWithLoading,
    IntegrationsHelpDialogKanbanAuto,
    IntegrationsHelpDialogKanban,
    Preloader,
    VacancyCandidatesKanban,
    PrefetchInterview,
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
  },
  setup(props, { root }) {
    const { vacancy } = toRefs(props);
    const { getCountCandidatesByStatus, countCandidatesByStatus } = useVacancyCandidatesByStatus();
    const { fetchPaginatedCandidates, deleteAll, getCandidatesByVacancyId } = useCandidates();
    const { getNotificationTemplatesSettings } = useNotificationTemplateSettings();
    const { useState } = createNamespacedHelpers('kanban');
    const { stages } = useState(['stages']);
    const { isExpired } = useSubscription();

    function redirectToUpgradePlan() {
      root.$router.push({
        name: 'settings',
        params: { type: settingsTabs.PAYMENT, addType: paymentTabs.INFO },
      });
    }

    const candidates = getCandidatesByVacancyId(vacancy);
    const operationLoader = ref(false);
    const isLoading = ref(true);
    const hasIntegrated = computed(() => {
      return vacancy.value?.externalVacancies?.length > 0;
    });

    const enableOperationLoader = () => {
      operationLoader.value = true;
    };

    const disableOperationLoader = () => {
      operationLoader.value = false;
    };

    function showMessagesSentToast(message) {
      nextTick(() => {
        if (Vue.prototype.$toast) {
          Vue.prototype.$toast.success(message || 'Сообщения отправлены');
        }
      });
    }

    async function getCountCandidates(search = '') {
      const result = await getCountCandidatesByStatus(vacancy.value.id, search);

      if (stages.value && stages.value.length > 0) {
        const serverStatusCounts = result?.statusCounts || [];
        const serverStatusMap = serverStatusCounts.reduce((acc, item) => {
          acc[item.status] = item.count || 0;
          return acc;
        }, {});

        const allStatusCounts = stages.value.map((stage) => {
          const countFromServer = serverStatusMap[stage.status];
          return {
            status: stage.status,
            count: countFromServer !== undefined ? countFromServer : 0,
          };
        });

        if (allStatusCounts.length > 0) {
          await VacancyCandidatesCountByStatus.insertOrUpdate({ data: allStatusCounts });
        }
      }
    }

    async function getPaginatedCandidates(payload = {
      page: 1,
      perPage: 10,
      search: '',
    }) {
      const { onComplete, ...requestPayload } = payload;
      try {
        const result = await fetchPaginatedCandidates(vacancy.value.id, requestPayload);
        const hasData = result?.response?.data?.data?.length > 0 || (Array.isArray(result?.response?.data) && result.response.data.length > 0);
        if (onComplete) {
          onComplete(hasData);
        }
      } catch (error) {
        if (onComplete) {
          onComplete(false);
        }
      }
    }

    onMounted(async () => {
      await deleteAll();
      await getPaginatedCandidates({ page: 1, perPage: 20, search: '' });
      await getCountCandidates('');
      await getNotificationTemplatesSettings();
      isLoading.value = false;
    });

    return {
      getPaginatedCandidates,
      candidates,
      isLoading,
      isExpired,
      redirectToUpgradePlan,
      autoInvitation: computed(() => {
        return vacancy.value.settings?.autoInvitation;
      }),
      hasIntegrated,
      operationLoader,
      enableOperationLoader,
      disableOperationLoader,
      showMessagesSentToast,
      countCandidatesByStatus,
      getCountCandidates,
    };
  },
};
</script>

<style lang="scss" scoped>
.vacancy-candidates-root {
  position: relative;
  height: 100%;
}

.vacancy-candidates-page--blurred {
  filter: blur(4px);
}

.vacancy-candidates-tariff-overlay {
  position: absolute;
  top: -48px;
  left: -48px;
  right: -48px;
  bottom: -48px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background: rgba(0, 0, 0, 0.55);
}

.vacancy-candidates-tariff-card {
  max-width: 400px;
  width: 100%;
  padding: 30px;
  border-radius: 8px;
}

.vacancy-candidates-tariff-header {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 0;
  margin-bottom: 8px;
}

.vacancy-candidates-tariff-icon {
  padding: 13px;
  background: rgba(234, 13, 13, 0.1);
  border-radius: 50%;
  margin-bottom: 20px;
}

.vacancy-candidates-tariff-title {
  font-weight: 400;
  font-size: 24px;
  line-height: 26px;
  color: #212121;
}

.vacancy-candidates-tariff-text {
  padding: 0;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 19.6px;
  font-weight: 300;
  color: #212121;
}

.vacancy-candidates-tariff-btn {
  width: 100%;
  font-weight: 400;
  font-size: 16px;
}

.vacancy-candidates__content {
  position: relative;
  min-height: 200px;
}

.vacancy-candidates__operation-preloader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.8);
}
</style>

<i18n locale="en" lang="yaml">
  candidates: Candidates
  tariffInactiveTitle: Your tariff is inactive.
  tariffInactiveText: To gain access to all the functions of your tariff, you need to make a payment.
  tariffInactiveButton: Upgrade plan
</i18n>

<i18n locale="ru" lang="yaml">
  candidates: Кандидаты
  tariffInactiveTitle: Тариф закончился
  tariffInactiveText: Для получения доступа к функциям Вашего тарифа необходимо внести оплату.
  tariffInactiveButton: Оплатить тариф
</i18n>
